import type {
  DisplayMinimumKey,
  DisplayMinimumView,
  DisplayMinimumWrite,
  InstrumentStatusView,
  MarketKind,
} from "@screener/protocol";
import { Button } from "@screener/uikit/components/button";
import { Input } from "@screener/uikit/components/input";
import { Label } from "@screener/uikit/components/label";
import { LoadingRegion } from "@screener/uikit/components/loading-skeleton";
import { SectionLabel } from "@screener/uikit/components/section-label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@screener/uikit/components/select";
import { Skeleton } from "@screener/uikit/components/skeleton";
import { useId, useState } from "react";
import {
  belowDaemonFloorLabel,
  DISPLAY_MINIMUMS_ADD,
  DISPLAY_MINIMUMS_BAD_COIN,
  DISPLAY_MINIMUMS_BAD_NUMBER,
  DISPLAY_MINIMUMS_COINS_HEADING,
  DISPLAY_MINIMUMS_EMPTY_PLACEHOLDER,
  DISPLAY_MINIMUMS_LOAD_FAILED,
  DISPLAY_MINIMUMS_LOADING,
  DISPLAY_MINIMUMS_MARKETS_HEADING,
  DISPLAY_MINIMUMS_MARKETS_HINT,
  DISPLAY_MINIMUMS_NO_COINS,
  DISPLAY_MINIMUMS_NO_MARKETS,
  DISPLAY_MINIMUMS_PICK_PAIR,
  DISPLAY_MINIMUMS_REJECTED,
  DISPLAY_MINIMUMS_REMOVE,
  DISPLAY_MINIMUMS_REMOVE_FAILED,
  DISPLAY_MINIMUMS_REMOVE_REJECTED,
  DISPLAY_MINIMUMS_RETRY,
  DISPLAY_MINIMUMS_SAVE,
  DISPLAY_MINIMUMS_SAVE_FAILED,
  exchangeLabel,
  marketLabel,
} from "@/lib/labels";
import {
  baseHints,
  type DisplayMinimumsState,
  findMinimum,
  floorForInput,
  isBelowDaemonFloor,
  type MarketPair,
  marketPairs,
  parseMinimumInput,
  resolveBase,
} from "./display-minimums";
import type { DisplayMinimumOutcome, DisplayMinimumPutResult } from "./use-display-minimums";

/**
 * Окно «Минимальный объём» §59 (design.md stage8-personal-display-filters,
 * Д6): два списка — минимумы рынков и минимумы монет. Часть экрана, не
 * UIKit: знает про протокол и про домен. Состояние списка и запросы держит
 * каркас (`useDisplayMinimums`), здесь только разметка и ввод.
 */

export interface DisplayMinimumsPanelProps {
  state: DisplayMinimumsState;
  /** Инструменты потока: из них берутся пары «биржа × рынок» и подсказки баз. */
  instruments: readonly InstrumentStatusView[];
  onPut: (write: DisplayMinimumWrite) => Promise<DisplayMinimumPutResult>;
  onRemove: (key: DisplayMinimumKey) => Promise<DisplayMinimumOutcome>;
  onReload: () => void;
}

function pairText(pair: MarketPair): string {
  return `${exchangeLabel(pair.exchange)} · ${marketLabel(pair.market)}`;
}

function pairValue(pair: MarketPair): string {
  return `${pair.exchange}:${pair.market}`;
}

function failureText(outcome: Exclude<DisplayMinimumOutcome, "ok">): string {
  return outcome === "invalid" ? DISPLAY_MINIMUMS_REJECTED : DISPLAY_MINIMUMS_SAVE_FAILED;
}

/** Своя подпись у неудачи снятия: «сохранить» и «проверьте число» тут были бы неправдой. */
function removeFailureText(outcome: Exclude<DisplayMinimumOutcome, "ok">): string {
  return outcome === "invalid" ? DISPLAY_MINIMUMS_REMOVE_REJECTED : DISPLAY_MINIMUMS_REMOVE_FAILED;
}

interface MinimumRowProps {
  /** Подпись строки целиком: «Coinbase · SPOT» или «Coinbase · SPOT · BTC». */
  label: string;
  existing: DisplayMinimumView | undefined;
  /** Порог демона, от которого помечается введённое число; `null` — пометки нет. */
  floorUsd: string | null;
  /** У минимума рынка очистка непустого поля — снятие записи; у монеты есть кнопка «Убрать». */
  clearRemoves: boolean;
  onSave: (minUsd: string) => Promise<DisplayMinimumOutcome>;
  onRemove: () => Promise<DisplayMinimumOutcome>;
}

function MinimumRow({
  label,
  existing,
  floorUsd,
  clearRemoves,
  onSave,
  onRemove,
}: MinimumRowProps) {
  const inputId = useId();
  const noteId = useId();
  const errorId = useId();
  const [draft, setDraft] = useState(existing?.min_usd ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const parsed = parseMinimumInput(draft);
  const note =
    parsed.ok && floorUsd !== null && isBelowDaemonFloor(parsed.value, floorUsd)
      ? belowDaemonFloorLabel(floorUsd)
      : null;
  const unchanged = draft.trim() === (existing?.min_usd ?? "");

  async function remove(): Promise<void> {
    setBusy(true);
    const outcome = await onRemove();
    setBusy(false);
    if (outcome === "ok") {
      setDraft("");
      setError(null);
      return;
    }
    setError(removeFailureText(outcome));
  }

  async function submit(): Promise<void> {
    if (draft.trim() === "" && clearRemoves) {
      if (existing !== undefined) {
        await remove();
      }
      return;
    }
    if (!parsed.ok) {
      setError(DISPLAY_MINIMUMS_BAD_NUMBER);
      return;
    }
    setBusy(true);
    const outcome = await onSave(parsed.value);
    setBusy(false);
    if (outcome === "ok") {
      // Поле берёт число из ответа сервера: строку записи пересоздаёт новый
      // `key` (в нём `min_usd`), черновик следует за записью.
      setError(null);
      return;
    }
    // Введённое число остаётся в поле: повторить можно тем же нажатием.
    setError(failureText(outcome));
  }

  return (
    <li className="flex flex-col gap-1">
      <form
        className="flex flex-col gap-1.5"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Label htmlFor={inputId} className="text-sm font-normal">
          {label}
        </Label>
        <div className="flex items-center gap-2">
          <Input
            id={inputId}
            inputMode="text"
            value={draft}
            placeholder={clearRemoves ? DISPLAY_MINIMUMS_EMPTY_PLACEHOLDER : "USD"}
            aria-invalid={error === DISPLAY_MINIMUMS_BAD_NUMBER ? true : undefined}
            aria-describedby={
              [note !== null ? noteId : null, error !== null ? errorId : null]
                .filter((id) => id !== null)
                .join(" ") || undefined
            }
            disabled={busy}
            onChange={(event) => {
              setDraft(event.target.value);
              setError(null);
            }}
          />
          <Button
            type="submit"
            size="sm"
            variant="outline"
            aria-label={`${DISPLAY_MINIMUMS_SAVE}: ${label}`}
            disabled={busy || (unchanged && !(draft.trim() === "" && existing !== undefined))}
          >
            {DISPLAY_MINIMUMS_SAVE}
          </Button>
          {!clearRemoves && existing !== undefined ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              aria-label={`${DISPLAY_MINIMUMS_REMOVE}: ${label}`}
              disabled={busy}
              onClick={() => void remove()}
            >
              {DISPLAY_MINIMUMS_REMOVE}
            </Button>
          ) : null}
        </div>
      </form>
      <p id={noteId} aria-live="polite" className="text-xs text-warning empty:hidden">
        {note}
      </p>
      {error !== null ? (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </li>
  );
}

interface AddCoinFormProps {
  pairs: readonly MarketPair[];
  instruments: readonly InstrumentStatusView[];
  minimums: readonly DisplayMinimumView[];
  onPut: (write: DisplayMinimumWrite) => Promise<DisplayMinimumOutcome>;
}

function AddCoinForm({ pairs, instruments, minimums, onPut }: AddCoinFormProps) {
  const pairId = useId();
  const baseId = useId();
  const numberId = useId();
  const hintsId = useId();
  // `undefined` — пользователь не выбирал, берётся первая пара; `null` — выбор
  // сброшен, потому что выбранная пара исчезла из списка.
  const [pairKey, setPairKey] = useState<string | null | undefined>(undefined);
  const [baseInput, setBaseInput] = useState("");
  const [numberInput, setNumberInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (pairs.length === 0) {
    return null;
  }
  const pair =
    pairKey === undefined ? pairs[0] : pairs.find((candidate) => pairValue(candidate) === pairKey);
  if (pairKey !== undefined && pairKey !== null && pair === undefined) {
    // Выбранная пара исчезла: молча подставить другую значило бы записать
    // минимум не туда, куда смотрел пользователь.
    setPairKey(null);
  }
  const hints = pair === undefined ? [] : baseHints(instruments, pair);
  const base = resolveBase(baseInput, hints);
  const parsed = parseMinimumInput(numberInput);
  const floorUsd = base === "" || pair === undefined ? null : floorForInput(minimums, pair, base);
  const note =
    parsed.ok && floorUsd !== null && isBelowDaemonFloor(parsed.value, floorUsd)
      ? belowDaemonFloorLabel(floorUsd)
      : null;
  const pairItems: Record<string, string> = Object.fromEntries(
    pairs.map((candidate) => [pairValue(candidate), pairText(candidate)]),
  );

  async function submit(): Promise<void> {
    if (base === "") {
      setError(DISPLAY_MINIMUMS_BAD_COIN);
      return;
    }
    if (!parsed.ok) {
      setError(DISPLAY_MINIMUMS_BAD_NUMBER);
      return;
    }
    if (pair === undefined) {
      return;
    }
    setBusy(true);
    const outcome = await onPut({
      exchange: pair.exchange,
      market: pair.market,
      base,
      min_usd: parsed.value,
    });
    setBusy(false);
    if (outcome === "ok") {
      setBaseInput("");
      setNumberInput("");
      setError(null);
      return;
    }
    setError(failureText(outcome));
  }

  return (
    <form
      className="flex flex-col gap-2 rounded-lg border border-border p-3"
      aria-label="Добавить минимум монеты"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <div className="flex flex-col gap-1">
        <Label htmlFor={pairId} className="text-xs text-muted-foreground">
          Биржа и рынок
        </Label>
        <Select<string>
          items={pairItems}
          value={pair === undefined ? null : pairValue(pair)}
          onValueChange={(value) => {
            setPairKey(value);
            setError(null);
          }}
          disabled={busy}
        >
          <SelectTrigger id={pairId} className="w-full">
            <SelectValue placeholder={DISPLAY_MINIMUMS_PICK_PAIR} />
          </SelectTrigger>
          <SelectContent>
            {pairs.map((candidate) => (
              <SelectItem key={pairValue(candidate)} value={pairValue(candidate)}>
                {pairText(candidate)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Label htmlFor={baseId} className="text-xs text-muted-foreground">
            Монета
          </Label>
          <Input
            id={baseId}
            list={hintsId}
            autoComplete="off"
            value={baseInput}
            placeholder="BTC"
            disabled={busy}
            onChange={(event) => {
              setBaseInput(event.target.value);
              setError(null);
            }}
          />
          <datalist id={hintsId}>
            {hints.map((hint) => (
              <option key={hint} value={hint} />
            ))}
          </datalist>
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Label htmlFor={numberId} className="text-xs text-muted-foreground">
            Минимум, USD
          </Label>
          <Input
            id={numberId}
            inputMode="text"
            value={numberInput}
            placeholder="USD"
            disabled={busy}
            onChange={(event) => {
              setNumberInput(event.target.value);
              setError(null);
            }}
          />
        </div>
      </div>
      {note !== null ? <p className="text-xs text-warning">{note}</p> : null}
      {error !== null ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
      <Button type="submit" size="sm" className="self-start" disabled={busy || pair === undefined}>
        {DISPLAY_MINIMUMS_ADD}
      </Button>
    </form>
  );
}

function marketKey(exchange: string, market: MarketKind): DisplayMinimumKey {
  return { exchange, market, base: null };
}

export function DisplayMinimumsPanel({
  state,
  instruments,
  onPut: putMinimum,
  onRemove,
  onReload,
}: DisplayMinimumsPanelProps) {
  // Общему окну нужен только исход: запись из ответа ему не нужна.
  const onPut = async (write: DisplayMinimumWrite): Promise<DisplayMinimumOutcome> =>
    (await putMinimum(write)).outcome;
  if (state.status === "loading") {
    return (
      <LoadingRegion label={DISPLAY_MINIMUMS_LOADING} className="flex flex-col gap-6">
        {Array.from({ length: 2 }, (_, section) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: статичные декоративные разделы
          <div key={section} className="flex flex-col gap-3">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-4 w-2/3" />
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-9 flex-1" />
              <Skeleton className="h-9 w-24" />
            </div>
          </div>
        ))}
      </LoadingRegion>
    );
  }
  if (state.status === "unavailable") {
    return (
      <div className="flex flex-col items-start gap-2">
        <p role="alert" className="text-sm text-destructive">
          {DISPLAY_MINIMUMS_LOAD_FAILED}
        </p>
        <Button size="sm" variant="outline" onClick={onReload}>
          {DISPLAY_MINIMUMS_RETRY}
        </Button>
      </div>
    );
  }

  const { minimums } = state;
  const pairs = marketPairs(instruments, minimums);
  const coinMinimums = minimums
    .filter((minimum) => minimum.base !== null)
    .sort(
      (left, right) =>
        left.exchange.localeCompare(right.exchange) ||
        left.market.localeCompare(right.market) ||
        (left.base ?? "").localeCompare(right.base ?? ""),
    );

  return (
    <div className="flex flex-col gap-6">
      <section aria-label={DISPLAY_MINIMUMS_MARKETS_HEADING} className="flex flex-col gap-3">
        <SectionLabel as="h2">{DISPLAY_MINIMUMS_MARKETS_HEADING}</SectionLabel>
        <p className="text-xs text-muted-foreground">{DISPLAY_MINIMUMS_MARKETS_HINT}</p>
        {pairs.length === 0 ? (
          <p className="text-sm text-muted-foreground">{DISPLAY_MINIMUMS_NO_MARKETS}</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {pairs.map((pair) => {
              const key = marketKey(pair.exchange, pair.market);
              const existing = findMinimum(minimums, key);
              return (
                <MinimumRow
                  key={`${pairValue(pair)}:${existing?.min_usd ?? ""}`}
                  label={pairText(pair)}
                  existing={existing}
                  floorUsd={floorForInput(minimums, pair, null)}
                  clearRemoves
                  onSave={(minUsd) => onPut({ ...key, min_usd: minUsd })}
                  onRemove={() => onRemove(key)}
                />
              );
            })}
          </ul>
        )}
      </section>

      <section aria-label={DISPLAY_MINIMUMS_COINS_HEADING} className="flex flex-col gap-3">
        <SectionLabel as="h2">{DISPLAY_MINIMUMS_COINS_HEADING}</SectionLabel>
        {coinMinimums.length === 0 ? (
          <p className="text-sm text-muted-foreground">{DISPLAY_MINIMUMS_NO_COINS}</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {coinMinimums.map((minimum) => {
              const key: DisplayMinimumKey = {
                exchange: minimum.exchange,
                market: minimum.market,
                base: minimum.base,
              };
              return (
                <MinimumRow
                  key={`${pairValue(minimum)}:${minimum.base}:${minimum.min_usd}`}
                  label={`${pairText(minimum)} · ${minimum.base}`}
                  existing={minimum}
                  floorUsd={minimum.daemon_floor_usd}
                  clearRemoves={false}
                  onSave={(minUsd) => onPut({ ...key, min_usd: minUsd })}
                  onRemove={() => onRemove(key)}
                />
              );
            })}
          </ul>
        )}
        <AddCoinForm pairs={pairs} instruments={instruments} minimums={minimums} onPut={onPut} />
      </section>
    </div>
  );
}
